'use client';

import React, { useMemo, useState } from "react";
import { CheckCircle2, XCircle, Smartphone, Search, Info } from "lucide-react";
import devicesJson from "@/data/esim-devices.json";
import BackgroundDecor from "@/components/BackgroundDecor";
import { useTranslations } from "@/lib/i18n-client";
import AppSelect from "@/components/ui/AppSelect";

type Device = { brand: string; model: string; esim: boolean; notes?: string; dualEsim?: boolean };
const devices: Device[] = devicesJson as Device[];

export default function Compatibility() {
  const { t } = useTranslations();
  const [q, setQ] = useState("");
  const brands = useMemo(() => Array.from(new Set(devices.map(d => d.brand))).sort(), []);
  const [brand, setBrand] = useState<string>("");

  const models = useMemo(() => devices.filter(d => d.brand === brand), [brand]);
  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!brand) return [] as Device[];
    if (!query) return models;
    return models.filter(m => m.model.toLowerCase().includes(query));
  }, [q, models, brand]);

  const pageSize = 24;
  const [page, setPage] = useState(0);
  const pageCount = Math.ceil(filtered.length / pageSize) || 1;
  const visibleCount = (page + 1) * pageSize;
  const visible = useMemo(() => filtered.slice(0, visibleCount), [filtered, visibleCount]);

  return (
    <section id="compatibility" className="py-16 relative pattern-overlay-subtle">
      <BackgroundDecor section="compatibility" seed={122} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl sm:text-4xl font-bold mb-3">{t("compatibility.title", "Device Compatibility")}</h2>
          <p className="text-xl text-muted-foreground">{t("compatibility.subtitle", "Check if your phone supports eSIM")}</p>
        </div>

        <div className="glass rounded-xl p-4 md:p-6 mb-6 card-pattern">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:items-center">
            <label htmlFor="compatibility-brand" className="text-sm text-muted-foreground md:col-span-1">{t("compatibility.filters.brand", "Brand")}</label>
            <div className="md:col-span-2">
              <AppSelect
                id="compatibility-brand"
                value={brand}
                ariaLabel={t("compatibility.filters.brandPlaceholder", "Select a brand")}
                placeholder={t("compatibility.filters.brandPlaceholder", "Select a brand")}
                onValueChange={(value) => { setBrand(value); setPage(0); }}
                options={[
                  { value: "", label: t("compatibility.filters.brandPlaceholder", "Select a brand") },
                  ...brands.map((b) => ({ value: b, label: b })),
                ]}
              />
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 md:items-center">
            <label htmlFor="compatibility-model" className="text-sm text-muted-foreground md:col-span-1">{t("compatibility.filters.model", "Model")}</label>
            <div className="md:col-span-2 flex items-center gap-2">
              <Search className="w-5 h-5 text-muted-foreground" />
              <input
                id="compatibility-model"
                className="flex-1 bg-transparent outline-none border-b border-border py-2"
                placeholder={t("compatibility.filters.modelPlaceholder", "Search model (e.g., iPhone 14)")}
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(0); }}
                disabled={!brand}
              />
            </div>
          </div>
        </div>

        {!brand ? (
          <div className="text-center text-muted-foreground">{t("compatibility.empty", "Select a brand to see compatible devices.")}</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {visible.map((d) => (
              <div key={d.brand + d.model} className="glass rounded-lg p-3 flex items-center gap-2">
                <Smartphone className="w-4 h-4 text-primary" />
                <div className="flex-1">
                  <div className="text-sm">{d.model}</div>
                  {d.notes && (
                    <div className="text-[10px] text-muted-foreground flex items-center gap-1">
                      <Info className="w-3 h-3" /> {d.notes}
                    </div>
                  )}
                </div>
                {d.esim ? (
                  <span className="ml-auto inline-flex items-center gap-1 text-xs text-success"><CheckCircle2 className="w-4 h-4" /> {t("compatibility.labels.esim", "eSIM")}</span>
                ) : (
                  <span className="ml-auto inline-flex items-center gap-1 text-xs text-destructive"><XCircle className="w-4 h-4" /> {t("compatibility.labels.noEsim", "No eSIM")}</span>
                )}
              </div>
            ))}
          </div>
        )}

        {brand && filtered.length > pageSize && (
          <div className="mt-6 flex flex-col items-center gap-3">
            {visibleCount < filtered.length ? (
              <button className="px-4 py-2 rounded-md border border-border bg-muted text-sm" onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))}>{t("compatibility.actions.loadMore", "Load more")}</button>
            ) : (
              <button className="px-4 py-2 rounded-md border border-border bg-muted text-sm" onClick={() => setPage(0)}>{t("compatibility.actions.showLess", "Show less")}</button>
            )}
            <div className="flex items-center gap-1">
              {Array.from({ length: pageCount }).map((_, i) => (
                <button
                  key={i}
                  aria-label={t("compatibility.aria.page", "Page {page}").replace("{page}", String(i + 1))}
                  onClick={() => setPage(i)}
                  className={`w-2.5 h-2.5 rounded-full ${i <= page ? 'bg-primary' : 'bg-muted'}`}
                />
              ))}
            </div>
          </div>
        )}

        <div className="mt-6 text-sm text-muted-foreground text-center">
          {t("compatibility.footer", "Not listed? If your device is eSIM-capable per the manufacturer, it will work with eSIMSupra.")}
        </div>
      </div>
    </section>
  );
}
